import React, { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps, Redirect, useHistory } from 'react-router-dom';
import { Formik, FormikErrors } from 'formik';
import _ from 'lodash';
import {
  CashieringView,
  CashieringRoute,
  WireType,
  CashieringManageAction,
  ScheduledTransferMechanism,
  ScheduledTransferDeliveryMethod,
  ScheduledTransferFrequencyUnit,
  AlertType,
  TransferInstruction,
  DateValueModel,
} from '@tradingblock/types';
import { AlertSection, getAlertProps, Loading } from '@tradingblock/components';
import {
  getCurrentTransferView,
  getTransferViewSubtitle,
  getTransferRouteTitle,
  getViewPrimaryButton,
  getNextTransferView,
  showPrimaryButton,
} from '../../utilities/cashiering';
import { CashieringViewFormValues, useCashieringView } from './cashiering/data/useCashieringView';
import { useCashieringDataContext, useCashieringData } from './cashiering/data/useCashieringData';
import { ScreenWrapper } from './ScreenWrapper';
import { TradingBlockLink } from '../../components/basic/TradingBlockLink';
import { useStateSelector } from '../../data/global/dataSelectors';
import { account } from '../../data/global/selectors/accountSelectors';
import { DataActions } from '../../data/global/actions';
import { useDispatch as useReduxDispatch } from 'react-redux';
import { CashieringActions } from '../../data/global/actions/CashieringActions';
import { cashieringSelectors } from '../../data/global/dataSelectors';
import { cleanDateModel } from '../../utilities/date';

interface CashieringScreenRouteProps {
  route: CashieringRoute;
}

type BaseCashieringViewProps = RouteComponentProps<CashieringScreenRouteProps> & {
  scheduledTransferMode?: boolean;
};

export const BaseCashieringView: React.FC<BaseCashieringViewProps> = ({ match, scheduledTransferMode }) => {
  const route = match.path === '/accounts/oauth' ? CashieringRoute.Accounts : match.params.route;
  return (
    <useCashieringDataContext.Provider>
      <BaseCashieringViewContent route={route} scheduledTransferMode={scheduledTransferMode} />
    </useCashieringDataContext.Provider>
  );
};

const BaseCashieringViewContent: React.FC<CashieringScreenRouteProps & { scheduledTransferMode?: boolean }> = ({
  route,
  scheduledTransferMode,
}) => {
  const view = getCurrentTransferView();

  const subAccountId = useStateSelector(account.subAccountId);
  const subAccounts = useStateSelector(account.subAccounts);
  const positionsLoaded = useStateSelector(s => s.positions.loaded);
  const accountId = useStateSelector(s => s.account.accountId);
  const history = useHistory();
  const dispatch = useReduxDispatch();
  const cashieringProfile = useStateSelector(s => s.cashiering.cashieringProfile);
  const transferInstructions = useStateSelector(cashieringSelectors.transferInstructions);
  const { iraConstraints } = cashieringProfile;
  const { contribution, distribution } = iraConstraints
    ? iraConstraints
    : { contribution: undefined, distribution: undefined };

  const {
    action,
    setAction,
    formStatus,
    setFormStatus,
    closeScreen,
    setCloseScreen,
    gotoNextView,
  } = useCashieringDataContext();

  const isNotEligible = useMemo(() => {
    if (!iraConstraints) return false;

    switch (route) {
      case CashieringRoute.Deposit: {
        if (!contribution) return false;

        // Check if any transfer method has allowed types
        const hasAllowedTypes = [
          contribution.ach && contribution.ach.allowedTypes && contribution.ach.allowedTypes.length > 0,
        ].some(hasTypes => hasTypes);

        return !hasAllowedTypes;
      }

      case CashieringRoute.Withdraw: {
        if (!distribution) return false;

        // Check if any transfer method has valid reasons
        const hasValidReasons = [
          distribution.ach && distribution.ach.validReasons && distribution.ach.validReasons.length > 0,
          distribution.check && distribution.check.validReasons && distribution.check.validReasons.length > 0,
          distribution.wire && distribution.wire.validReasons && distribution.wire.validReasons.length > 0,
        ].some(hasReasons => hasReasons);

        return !hasValidReasons;
      }

      default:
        return false;
    }
  }, [route, iraConstraints, contribution, distribution]);

  const { isSaving, isFetching } = useCashieringData(route, view);

  const [TransferViewComponent, onViewSubmit, onViewValidate] = useCashieringView(route, view, action);

  useEffect(() => {
    if (!positionsLoaded) {
      dispatch(DataActions.requestPositions({ accountId, more: true }, { debounce: 1000 }));
    }
  }, [positionsLoaded, accountId, dispatch]);

  const isPrimaryButtonDisabled = useCallback(
    (errors: FormikErrors<CashieringViewFormValues>) => {
      // disable primary continue button on authorize view if field errors present (i.e. empty field)
      if (view === CashieringView.Authorize && action !== CashieringManageAction.Close) {
        return !_.isEmpty(errors);
      }

      // disable primary continue button on amount view if not eligible
      if (view === CashieringView.Amount && isNotEligible) {
        return true;
      }

      // default to not disabling the button
      return false;
    },
    [view, action, isNotEligible]
  );

  const routeTitle = useMemo(() => getTransferRouteTitle(route), [route]);
  const viewSubtitle = useMemo(() => getTransferViewSubtitle(view), [view]);
  const showCancel = useMemo(() => view === CashieringView.Review || action === CashieringManageAction.Delete, [
    view,
    action,
  ]);
  const showConfirmClose = useMemo(() => _.isUndefined(closeScreen), [closeScreen]);

  const onClose = useCallback(() => {
    const confirmationNeeded = !_.includes(
      [CashieringView.Authorize, CashieringView.Amount, CashieringView.Manage],
      view
    );
    // if we need to ask for confirmation of closing screen, set closeScreen = undefined
    if (confirmationNeeded || (action && action !== CashieringManageAction.Close)) {
      setCloseScreen(undefined);
    } else {
      setCloseScreen(true);
    }
  }, [view, action]);

  const onCancelClick = useCallback(() => {
    // cancel current action, otherwise, treat like close click
    if (action) {
      setAction(undefined);
    } else {
      onClose();
    }
  }, [action]);

  const onValidate = (values: CashieringViewFormValues) => {
    let errors: Record<string, string> = {};
    if (onViewValidate) {
      errors = (onViewValidate(values) as Record<string, string>) || {};
    }

    // Add scheduled transfer validation
    if (scheduledTransferMode && (view === CashieringView.Amount || view === CashieringView.ScheduledTransferAmount)) {
      if (!values.transfer || !values.transfer.nickName) {
        errors['transfer.nickName'] = 'Please enter a nickname.';
      }
      if (!values.transfer || !values.transfer.frequencyInterval) {
        errors['transfer.frequencyInterval'] = 'Please enter the interval.';
      }
      if (!values.transfer || !values.transfer.frequencyUnit) {
        errors['transfer.frequencyUnit'] = 'Please select the frequency unit.';
      }
      if (!values.transfer || !values.transfer.startDate) {
        errors['transfer.startDate'] = 'Please select a start date.';
      } else {
        let startDateObj: Date | null = null;
        const raw = values.transfer.startDate;

        if (typeof raw === 'string') {
          startDateObj = new Date(raw);
        } else if (
          typeof raw === 'object' &&
          raw !== null &&
          'year' in raw &&
          typeof (raw as any).year === 'number' &&
          'month' in raw &&
          typeof (raw as any).month === 'number' &&
          'day' in raw &&
          typeof (raw as any).day === 'number'
        ) {
          const { year, month, day } = raw as { year: number; month: number; day: number };
          startDateObj = new Date(year, month - 1, day);
        }

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        if (!startDateObj || isNaN(startDateObj.getTime())) {
          errors['transfer.startDate'] = 'Please select a valid start date.';
        } else if (startDateObj <= now) {
          errors['transfer.startDate'] = 'Start date must be after today.';
        }
      }
      // occurrences is intentionally not required
    }

    return errors;
  };

  const handleScheduledTransferSubmit = useCallback(
    (values: CashieringViewFormValues) => {
      if (!values.transfer) {
        console.error('Missing transfer values');
        return false;
      }

      const {
        mechanism,
        direction,
        amount,
        frequencyInterval,
        frequencyUnit,
        occurrences,
        startDate,
        instructionId,
        achRelationshipId,
        nickName,
      } = values.transfer;

      // Validate required fields
      const requiredFields = {
        accountId,
        amount,
        frequencyInterval,
        frequencyUnit,
        startDate,
      };

      const missingFields = Object.entries(requiredFields)
        .filter(([_, value]) => !value)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields.join(', '));
        return false;
      }

      // At this point we know all required fields are present
      const validatedStartDate = cleanDateModel(startDate as DateValueModel);
      const validatedFrequencyInterval = frequencyInterval as number;
      const validatedFrequencyUnit = frequencyUnit as ScheduledTransferFrequencyUnit;
      const validatedOccurrences = occurrences as number;
      const validatedAmount = amount as number;
      const validatedNickName = nickName as string;

      // Validate date is in the future
      if (!validatedStartDate) {
        console.error('Start date is required');
        return false;
      }
      const startDateObj = new Date(validatedStartDate);
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of today
      if (startDateObj <= now) {
        console.error('Start date must be after today');
        return false;
      }

      // Validate frequency interval (must be >= 1)
      if (validatedFrequencyInterval < 1) {
        console.error('Frequency interval must be greater than 0');
        return false;
      }
      // occurrences can be 0, empty, or undefined (meaning until cancelled)

      // Clean IRA Contribution for scheduled deposits
      const cleanIraContribution = (iraContribution: any) => {
        if (!iraContribution) return undefined;
        return {
          type: iraContribution.type,
          applyToPriorYear: !!iraContribution.applyToPriorYear,
        };
      };

      // Clean IRA Distribution for scheduled withdrawals
      const cleanIraDistribution = (distribution: any, transferValues: CashieringViewFormValues) => {
        if (!distribution) {
          return distribution;
        }

        const cleanedDistribution = _.cloneDeep(distribution);

        // Helper function to handle tax withholding cleanup
        const cleanTaxWithholding = (
          withholding: any,
          requirements: any,
          iraDistribution: any,
          type: 'federalTaxWithholding' | 'stateTaxWithholding'
        ) => {
          if (!withholding || !withholding.value || withholding.value === 0) {
            if (
              requirements &&
              requirements.withholdingOption === 'Mandatory' &&
              (requirements.requiredMinimum === 'PercentageOfFederal' ||
                requirements.requiredMinimum === 'NoMinimum') &&
              iraDistribution &&
              iraDistribution[type] &&
              iraDistribution[type].valueType
            ) {
              cleanedDistribution[type].value = 0;
            } else {
              delete cleanedDistribution[type];
            }
          } else if (withholding.valueType === 'Percentage') {
            // Convert percentage to decimal if needed
            cleanedDistribution[type].value = withholding.value / 100;
          }
        };

        // Handle formatting date of death, if applicable ({ month, day, year } => MM/DD/YYYY)
        if (cleanedDistribution.dateOfDeath) {
          const { month, day, year } = cleanedDistribution.dateOfDeath;
          cleanedDistribution.dateOfDeath = `${month}/${day}/${year}`;
        }

        // Handle federal tax withholding
        cleanTaxWithholding(
          cleanedDistribution.federalTaxWithholding,
          cashieringProfile &&
            cashieringProfile.iraConstraints &&
            cashieringProfile.iraConstraints.stateTaxWithholdingRequirements,
          transferValues.transfer && transferValues.transfer.iraDistribution,
          'federalTaxWithholding'
        );

        // Handle state tax withholding
        cleanTaxWithholding(
          cleanedDistribution.stateTaxWithholding,
          cashieringProfile &&
            cashieringProfile.iraConstraints &&
            cashieringProfile.iraConstraints.stateTaxWithholdingRequirements,
          transferValues.transfer && transferValues.transfer.iraDistribution,
          'stateTaxWithholding'
        );

        // Handle receiving institution name
        if (cleanedDistribution.reason === 'ApexTransfer') {
          if (mechanism === 'Wire' && instructionId && transferInstructions) {
            const instruction = _.find(transferInstructions, (i: TransferInstruction) => i.id === instructionId) as
              | TransferInstruction
              | undefined;
            cleanedDistribution.receivingInstitutionName = instruction ? instruction.name : '';
          } else if (mechanism === 'Check' && transferValues.transfer && transferValues.transfer.nameOnAccount) {
            cleanedDistribution.receivingInstitutionName = transferValues.transfer.nameOnAccount;
          }
        }

        return cleanedDistribution;
      };

      // Include all fields from values.transfer to ensure iraContribution/iraDistribution are sent
      const baseRequest = {
        ...values.transfer,
        accountId,
        frequencyInterval: validatedFrequencyInterval,
        frequencyUnit: validatedFrequencyUnit,
        occurrences: validatedOccurrences,
        startDate: validatedStartDate,
        nickName: validatedNickName,
        iraContribution: cleanIraContribution(values.transfer.iraContribution),
        iraDistribution: cleanIraDistribution(values.transfer.iraDistribution, values),
      };

      try {
        if (route === CashieringRoute.Deposit) {
          // Handle scheduled deposit
          if (mechanism === ScheduledTransferMechanism.Ach) {
            if (!achRelationshipId) {
              console.error('Missing ACH relationship ID for scheduled deposit');
              return false;
            }
            dispatch(
              CashieringActions.requestCreateAchScheduledDeposit({
                ...baseRequest,
                relationshipId: achRelationshipId,
                amount: validatedAmount,
              })
            );
          } else {
            console.error('Only ACH deposits are supported for scheduled transfers');
            return false;
          }
        } else {
          // Handle scheduled withdrawal
          if (mechanism === ScheduledTransferMechanism.Ach) {
            if (!achRelationshipId) {
              console.error('Missing ACH relationship ID for scheduled withdrawal');
              return false;
            }
            dispatch(
              CashieringActions.requestCreateAchScheduledWithdrawal({
                ...baseRequest,
                relationshipId: achRelationshipId,
                requestedAmount: validatedAmount,
              })
            );
          } else if (mechanism === ScheduledTransferMechanism.Wire) {
            if (!instructionId) {
              console.error('Missing instruction ID for scheduled wire withdrawal');
              return false;
            }
            dispatch(
              CashieringActions.requestCreateWireScheduledWithdrawal({
                ...baseRequest,
                instructionId,
                requestedAmount: validatedAmount,
              })
            );
          } else if (mechanism === ScheduledTransferMechanism.Check) {
            if (!instructionId) {
              console.error('Missing instruction ID for scheduled check withdrawal');
              return false;
            }
            dispatch(
              CashieringActions.requestCreateCheckScheduledWithdrawal({
                ...baseRequest,
                deliveryMethod: ScheduledTransferDeliveryMethod.Standard,
                requestedAmount: validatedAmount,
              })
            );
          } else {
            console.error('Unsupported mechanism for scheduled withdrawal:', mechanism);
            return false;
          }
        }
        return true;
      } catch (error) {
        console.error('Error submitting scheduled transfer:', error);
        return false;
      }
    },
    [dispatch, accountId, route, cashieringProfile, transferInstructions]
  );

  const onSubmit = useCallback(
    (values: CashieringViewFormValues) => {
      if (view === CashieringView.Review) {
        if (scheduledTransferMode) {
          // Scheduled transfer submit logic
          const success = handleScheduledTransferSubmit(values);
          if (success) {
            setCloseScreen(true);
          } else {
            setFormStatus({
              view,
              submitted: true,
              alertType: AlertType.danger,
              message: 'Failed to submit scheduled transfer',
            });
          }
          return;
        } else if (onViewSubmit) {
          // Regular transfer submit logic
          const success = onViewSubmit(values, view);
          if (success) {
            setCloseScreen(true);
          }
          return;
        }
      }
      // Existing scheduled transfer logic for authorize view
      if (scheduledTransferMode && view === CashieringView.Authorize) {
        if (onViewSubmit) {
          const success = onViewSubmit(values, view);
          if (success) {
            const nextView = getNextTransferView(view, route);
            if (nextView) {
              gotoNextView(nextView, history);
            }
          }
        }
        return;
      }
      // For other views in scheduled transfer mode, check if there's an active action first
      if (scheduledTransferMode) {
        // If there's an active action (like adding ACH relationship or wire instruction),
        // let the view handle it first before navigating
        if (action) {
          if (onViewSubmit) {
            const success = onViewSubmit(values, view);
            if (success) {
              // Action was handled successfully, now navigate to next view
              const nextView = getNextTransferView(view, route);
              if (nextView) {
                gotoNextView(nextView, history);
              }
            }
          }
          return;
        }
        // No active action, just navigate to the next view
        const nextView = getNextTransferView(view, route);
        if (nextView) {
          gotoNextView(nextView, history);
        }
        return;
      }
      // Default regular transfer logic
      if (onViewSubmit) {
        const success = onViewSubmit(values, view);
        if (success) {
          setCloseScreen(true);
        }
      } else {
        const nextView = getNextTransferView(view, route);
        if (nextView) {
          gotoNextView(nextView, history);
        }
      }
    },
    [route, view, onViewSubmit, history, scheduledTransferMode, handleScheduledTransferSubmit]
  );

  const onHandleSubmit = (handler: () => void, e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }
    // set current view and submitted flag, clearing any previous status messages
    setFormStatus({ view, submitted: true });
    // fire formik handler
    handler();
  };

  const onHandleSubmitClick = (handler: () => void, e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    onHandleSubmit(handler);
  };

  if (closeScreen) {
    return <Redirect to="/" />;
  }

  // send to default view if not set or trying to skip ahead without saving
  if (route && (!view || (view !== CashieringView.Authorize && !formStatus.view))) {
    window.location.hash = CashieringView.Authorize;
  }

  return (
    <ScreenWrapper
      showConfirmClose={showConfirmClose}
      screenType="transfer"
      onClose={() => setCloseScreen(true)}
      onCancel={() => setCloseScreen(false)}
    >
      <div className="screen-header">
        <div className="screen-title">
          <span className="txt-lg">
            {routeTitle}
            {viewSubtitle ? ` - ${viewSubtitle}` : ''}
          </span>
        </div>
        <div className="screen-help">
          <div className="screen-help-long">
            Need help? <TradingBlockLink to="CustomerServiceUrl">Contact us</TradingBlockLink>
          </div>
          <div className="screen-help-short">
            <TradingBlockLink to="CustomerServiceUrl">
              <i className="fal fa-comment-lines" title="Need help? Contact us." />
            </TradingBlockLink>
          </div>
        </div>
        <div className="screen-actions">
          <button className="close" type="button" onClick={onClose}>
            Cancel <i className="fal fa-times" />
          </button>
        </div>
      </div>

      <div className="screen-body">
        <Formik
          initialValues={{
            transfer: {
              type: WireType.Domestic,
              disbursementType: 'PartialBalance',
              mechanism: 'Ach',
              subAccountId,
              subAccountsValues: {},
              frequencyUnit: undefined,
              frequencyInterval: 1,
              occurrences: 1,
              startDate: undefined,
              amount: undefined,
            },
          }}
          enableReinitialize={false}
          validateOnChange={false}
          validateOnBlur={true}
          validate={onValidate}
          onSubmit={onSubmit}
        >
          {({ values, setValues, handleSubmit, touched, errors }) => (
            <form id="transfer" onSubmit={e => onHandleSubmit(handleSubmit, e)}>
              <TransferViewComponent
                values={values}
                setValues={setValues}
                {...(scheduledTransferMode && { scheduledTransferMode })}
              >
                {!isSaving && (formStatus.submitted || formStatus.alertType) && (
                  <>
                    <AlertSection {...getAlertProps(errors, formStatus)} />
                  </>
                )}
              </TransferViewComponent>
              {showPrimaryButton({ view, route, values, action }) && (
                <div className={`screen-footer ${isFetching || showCancel ? 'screen-footer-justified' : ''}`}>
                  {isFetching && <Loading size="small" />}
                  {!isFetching && showCancel && (
                    <button className="btn btn-blend" disabled={isSaving} onClick={onCancelClick} type="button">
                      <i className="fal fa-arrow-left" />{' '}
                      {action === CashieringManageAction.Delete
                        ? 'Do not delete'
                        : `Cancel ${route === CashieringRoute.Withdraw ? 'withdrawal' : route}`}
                    </button>
                  )}
                  {getViewPrimaryButton({ view, route, action, values }, isPrimaryButtonDisabled(errors), isSaving, e =>
                    onHandleSubmitClick(handleSubmit, e)
                  )}
                </div>
              )}
            </form>
          )}
        </Formik>
      </div>
    </ScreenWrapper>
  );
};
